import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { useSupabase } from '@/lib/supabase-context'
import { useOrganizacion } from '@/core/providers/OrganizacionProvider'
import { chatService, CHAT_ADJUNTO_URL_TTL_SECONDS } from '../services/chatService'
import { toast } from 'sonner'
import type { Mensaje } from '../types/chat'

// ===========================================
// Query Keys
// ===========================================

export const chatKeys = {
  all: ['chat'] as const,
  conversaciones: (orgId: string) => [...chatKeys.all, 'conv', orgId] as const,
  mensajes: (convId: string) => [...chatKeys.all, 'msgs', convId] as const,
  noLeidos: (email: string) => [...chatKeys.all, 'unread', email] as const,
  misConversacionIds: (email: string) => [...chatKeys.all, 'my-conv-ids', email] as const,
  readReceipts: (convId: string) => [...chatKeys.all, 'read-receipts', convId] as const,
  miembros: (convId: string) => [...chatKeys.all, 'members', convId] as const,
  busqueda: (convId: string | undefined, query: string) => [...chatKeys.all, 'search', convId ?? 'global', query] as const,
  attachmentUrl: (path: string) => [...chatKeys.all, 'attachment-url', path] as const,
}

// ===========================================
// Query Hooks
// ===========================================

export function useConversaciones() {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { user } = useAuth0()
  const { membresia } = useOrganizacion()
  const orgId = membresia?.organizacion_id ?? ''

  return useQuery({
    queryKey: chatKeys.conversaciones(orgId),
    queryFn: () => chatService.listarConversaciones(supabase!, orgId, user!.email!),
    // C9: orgId !== '' en vez de !!orgId (string vacío es truthy)
    enabled: !!supabase && !supabaseLoading && !!user?.email && orgId !== '',
    staleTime: 30_000,
  })
}

export function useMensajes(conversacionId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { user } = useAuth0()

  return useQuery({
    queryKey: chatKeys.mensajes(conversacionId),
    queryFn: () => chatService.obtenerMensajes(supabase!, conversacionId, user!.email!),
    enabled: !!supabase && !supabaseLoading && !!conversacionId && !!user?.email,
    // Broadcast maneja updates real-time. Solo fetch al montar (gap recovery)
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchInterval: false,
  })
}

// ===========================================
// Mutation Hooks
// ===========================================

// Counter for guaranteed unique temp IDs
let tempCounter = 0

export function useEnviarMensaje() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { membresia } = useOrganizacion()

  return useMutation({
    mutationFn: async (params: { conversacionId: string; content: string }) => {
      return chatService.enviarMensaje(supabase!, {
        conversacion_id: params.conversacionId,
        sender_email: user!.email!,
        content: params.content,
      })
    },
    onMutate: async (params) => {
      await queryClient.cancelQueries({ queryKey: chatKeys.mensajes(params.conversacionId) })

      const previousMessages = queryClient.getQueryData<Mensaje[]>(chatKeys.mensajes(params.conversacionId))

      // C6: Counter + timestamp para ID único garantizado
      const tempId = `temp-${++tempCounter}-${Date.now()}`
      const optimisticMessage: Mensaje = {
        id: tempId,
        conversacion_id: params.conversacionId,
        sender_email: user!.email!,
        content: params.content,
        deleted_at: null,
        created_at: new Date().toISOString(),
        attachment_url: null,
        attachment_path: null,
        attachment_type: null,
        attachment_name: null,
        attachment_size: null,
      }

      queryClient.setQueryData<Mensaje[]>(
        chatKeys.mensajes(params.conversacionId),
        (old) => [optimisticMessage, ...(old ?? [])]
      )

      return { previousMessages, tempId }
    },
    onSuccess: (realMessage, params, context) => {
      // C8: Remover temp + agregar real, evitando duplicados
      queryClient.setQueryData<Mensaje[]>(
        chatKeys.mensajes(params.conversacionId),
        (old) => {
          if (!old) return [realMessage]
          const filtered = old.filter((m) => m.id !== context?.tempId && m.id !== realMessage.id)
          return [realMessage, ...filtered]
        }
      )
      // C2: Usar orgId real, no string vacío
      const orgId = membresia?.organizacion_id
      if (orgId) {
        queryClient.invalidateQueries({ queryKey: chatKeys.conversaciones(orgId) })
      }
    },
    onError: (_err, params, context) => {
      if (context?.previousMessages) {
        queryClient.setQueryData(chatKeys.mensajes(params.conversacionId), context.previousMessages)
      }
      toast.error('Error al enviar mensaje')
    },
  })
}

export function useObtenerOCrearConversacion() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { membresia } = useOrganizacion()

  return useMutation({
    mutationFn: async (otherEmail: string) => {
      const orgId = membresia!.organizacion_id
      return chatService.obtenerOCrearDirecta(supabase!, orgId, user!.email!, otherEmail)
    },
    onSuccess: () => {
      const orgId = membresia?.organizacion_id ?? ''
      queryClient.invalidateQueries({ queryKey: chatKeys.conversaciones(orgId) })
    },
  })
}

export function useMarcarLeido() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { membresia } = useOrganizacion()

  return useMutation({
    mutationFn: async (params: { conversacionId: string; messageId: string }) => {
      await chatService.marcarLeido(supabase!, params.conversacionId, user!.email!, params.messageId)
    },
    onSuccess: () => {
      // Invalidar lista de conversaciones para refrescar no_leidos via RPC.
      // El unreadCount del ChatProvider se deriva de ese estado.
      const orgId = membresia?.organizacion_id
      if (orgId) {
        queryClient.invalidateQueries({ queryKey: chatKeys.conversaciones(orgId) })
      }
    },
    onError: (error) => {
      // Silencioso por diseño: si falla, el próximo mensaje reintenta.
      // No mostramos toast para no molestar con errores de background.
      console.error('Error marcando conversación como leída:', error)
    },
  })
}

export function useReadReceipts(conversacionId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: chatKeys.readReceipts(conversacionId),
    queryFn: () => chatService.obtenerReadReceipts(supabase!, conversacionId),
    enabled: !!supabase && !supabaseLoading && !!conversacionId,
    staleTime: 30_000,
  })
}

/**
 * Set of conversation IDs where current user is a member.
 * Used by ChatProvider for client-side notification filtering.
 */
export function useMisConversacionIds() {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { user } = useAuth0()

  return useQuery({
    queryKey: chatKeys.misConversacionIds(user?.email ?? ''),
    queryFn: () => chatService.obtenerMisConversacionIds(supabase!, user!.email!),
    enabled: !!supabase && !supabaseLoading && !!user?.email,
    staleTime: 30_000,
  })
}

export function useBuscarMensajes(query: string, conversacionId?: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()
  const { user } = useAuth0()
  const { membresia } = useOrganizacion()
  const orgId = membresia?.organizacion_id ?? ''

  return useQuery({
    queryKey: chatKeys.busqueda(conversacionId, query),
    queryFn: () => chatService.buscarMensajes(supabase!, query, {
      conversacionId,
      userEmail: user!.email!,
      organizacionId: orgId,
    }),
    // H4: requiere orgId para búsqueda scoped por organización
    enabled: !!supabase && !supabaseLoading && !!user?.email && orgId !== '' && query.length >= 2,
    staleTime: 10_000,
  })
}

export function useToggleMute() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { membresia } = useOrganizacion()

  return useMutation({
    mutationFn: async (conversacionId: string) => {
      return chatService.toggleMute(supabase!, conversacionId, user!.email!)
    },
    onSuccess: (muted) => {
      const orgId = membresia?.organizacion_id ?? ''
      queryClient.invalidateQueries({ queryKey: chatKeys.conversaciones(orgId) })
      toast.success(muted ? 'Conversación silenciada' : 'Notificaciones activadas')
    },
    onError: () => toast.error('Error al cambiar silencio'),
  })
}

export function useCrearGrupo() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { membresia } = useOrganizacion()

  return useMutation({
    mutationFn: async (input: { nombre: string; miembros: string[] }) => {
      const orgId = membresia!.organizacion_id
      return chatService.crearGrupo(supabase!, orgId, user!.email!, input)
    },
    onSuccess: () => {
      const orgId = membresia?.organizacion_id ?? ''
      queryClient.invalidateQueries({ queryKey: chatKeys.conversaciones(orgId) })
      queryClient.invalidateQueries({ queryKey: chatKeys.misConversacionIds(user!.email!) })
      toast.success('Grupo creado')
    },
    onError: (error: Error) => toast.error(error.message || 'Error al crear grupo'),
  })
}

export function useCrearChannel() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { membresia } = useOrganizacion()

  return useMutation({
    mutationFn: async (nombre: string) => {
      const orgId = membresia!.organizacion_id
      // Get all org member emails
      const { data: members } = await supabase!
        .from('usuario_organizaciones')
        .select('usuarios_brainhub(email)')
        .eq('organizacion_id', orgId)
        .eq('activo', true)
      type MemberRow = { usuarios_brainhub: { email: string | null } | null }
      const emails = ((members ?? []) as MemberRow[])
        .map((m) => m.usuarios_brainhub?.email)
        .filter((email): email is string => Boolean(email))
      return chatService.crearChannel(supabase!, orgId, user!.email!, nombre, emails)
    },
    onSuccess: () => {
      const orgId = membresia?.organizacion_id ?? ''
      queryClient.invalidateQueries({ queryKey: chatKeys.conversaciones(orgId) })
      queryClient.invalidateQueries({ queryKey: chatKeys.misConversacionIds(user!.email!) })
      toast.success('Canal creado')
    },
    onError: (error: Error) => toast.error(error.message || 'Error al crear canal'),
  })
}

// ===========================================
// Group Member Management
// ===========================================

export function useMiembrosGrupo(conversacionId: string) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: chatKeys.miembros(conversacionId),
    queryFn: () => chatService.listarMiembros(supabase!, conversacionId),
    enabled: !!supabase && !supabaseLoading && !!conversacionId,
    staleTime: 30_000,
  })
}

export function useAgregarMiembro() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { membresia } = useOrganizacion()

  return useMutation({
    mutationFn: async ({ conversacionId, email }: { conversacionId: string; email: string }) => {
      await chatService.agregarMiembro(supabase!, conversacionId, user!.email!, email)
    },
    onSuccess: (_, { conversacionId }) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.miembros(conversacionId) })
      queryClient.invalidateQueries({ queryKey: chatKeys.conversaciones(membresia?.organizacion_id ?? '') })
      queryClient.invalidateQueries({ queryKey: chatKeys.misConversacionIds(user!.email!) })
      toast.success('Miembro agregado')
    },
    onError: (error: Error) => toast.error(error.message || 'Error al agregar miembro'),
  })
}

export function useQuitarMiembro() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { membresia } = useOrganizacion()

  return useMutation({
    mutationFn: async ({ conversacionId, email }: { conversacionId: string; email: string }) => {
      await chatService.quitarMiembro(supabase!, conversacionId, user!.email!, email)
    },
    onSuccess: (_, { conversacionId }) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.miembros(conversacionId) })
      queryClient.invalidateQueries({ queryKey: chatKeys.conversaciones(membresia?.organizacion_id ?? '') })
      queryClient.invalidateQueries({ queryKey: chatKeys.misConversacionIds(user!.email!) })
      toast.success('Miembro removido')
    },
    onError: (error: Error) => toast.error(error.message || 'Error al quitar miembro'),
  })
}

export function useCambiarRol() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { user } = useAuth0()

  return useMutation({
    mutationFn: async ({ conversacionId, email, role }: { conversacionId: string; email: string; role: 'admin' | 'member' }) => {
      await chatService.cambiarRol(supabase!, conversacionId, user!.email!, email, role)
    },
    onSuccess: (_, { conversacionId }) => {
      queryClient.invalidateQueries({ queryKey: chatKeys.miembros(conversacionId) })
      toast.success('Rol actualizado')
    },
    onError: (error: Error) => toast.error(error.message || 'Error al cambiar rol'),
  })
}

// ===========================================
// File Attachments
// ===========================================

export function useSubirArchivo() {
  const queryClient = useQueryClient()
  const { supabase } = useSupabase()
  const { user } = useAuth0()
  const { membresia } = useOrganizacion()

  return useMutation({
    mutationFn: async ({ file, conversacionId }: { file: File; conversacionId: string }) => {
      const orgId = membresia!.organizacion_id
      const attachment = await chatService.subirArchivo(supabase!, file, conversacionId, orgId)

      return chatService.enviarMensaje(supabase!, {
        conversacion_id: conversacionId,
        sender_email: user!.email!,
        content: attachment.name,
        attachment_path: attachment.path,
        attachment_type: attachment.type,
        attachment_name: attachment.name,
        attachment_size: attachment.size,
      })
    },
    // Optimistic update: preview local inmediato durante el upload.
    // El blob URL se renderiza directo en ChatAttachment (detecta "blob:" prefix)
    // y se revoca cuando el mensaje real llega del servidor.
    onMutate: async ({ file, conversacionId }) => {
      await queryClient.cancelQueries({ queryKey: chatKeys.mensajes(conversacionId) })
      const previous = queryClient.getQueryData<Mensaje[]>(chatKeys.mensajes(conversacionId))

      const tempId = `temp-upload-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      const localUrl = URL.createObjectURL(file)
      const optimistic: Mensaje = {
        id: tempId,
        conversacion_id: conversacionId,
        sender_email: user!.email!,
        content: file.name,
        deleted_at: null,
        created_at: new Date().toISOString(),
        attachment_url: null,
        attachment_path: localUrl, // ← blob URL para preview local
        attachment_type: file.type,
        attachment_name: file.name,
        attachment_size: file.size,
      }

      queryClient.setQueryData<Mensaje[]>(
        chatKeys.mensajes(conversacionId),
        (old) => [...(old ?? []), optimistic],
      )

      return { previous, tempId, localUrl, conversacionId }
    },
    onSuccess: (_, { conversacionId }, context) => {
      const orgId = membresia?.organizacion_id ?? ''
      if (context?.localUrl) URL.revokeObjectURL(context.localUrl)
      queryClient.invalidateQueries({ queryKey: chatKeys.mensajes(conversacionId) })
      queryClient.invalidateQueries({ queryKey: chatKeys.conversaciones(orgId) })
    },
    onError: (error: Error, _vars, context) => {
      // Revertir optimistic update
      if (context?.previous) {
        queryClient.setQueryData(chatKeys.mensajes(context.conversacionId), context.previous)
      }
      if (context?.localUrl) URL.revokeObjectURL(context.localUrl)
      toast.error(error.message || 'Error al enviar archivo')
    },
  })
}

/**
 * Hook que genera un signed URL on-demand para un attachment.
 * Renueva la URL 2 min antes de que venza, también con la pantalla quieta. Si la renovación
 * falla, reintenta cada 30 s: la URL anterior vence antes de la próxima renovación normal.
 * @param path - attachment_path del mensaje (null/undefined → hook disabled)
 */
export function useAttachmentUrl(path: string | null | undefined) {
  const { supabase, isLoading: supabaseLoading } = useSupabase()

  return useQuery({
    queryKey: chatKeys.attachmentUrl(path ?? ''),
    queryFn: () => chatService.obtenerAttachmentUrl(supabase!, path!),
    enabled: !!supabase && !supabaseLoading && !!path,
    staleTime: (CHAT_ADJUNTO_URL_TTL_SECONDS - 120) * 1000,
    refetchInterval: (query) =>
      query.state.status === 'error' ? 30_000 : (CHAT_ADJUNTO_URL_TTL_SECONDS - 120) * 1000,
    gcTime: CHAT_ADJUNTO_URL_TTL_SECONDS * 1000,
    retry: 1,
  })
}
